import { useCallback, useEffect, useState } from 'react';
import { Button, Input, Card, Alert, Loader, Badge, Table, Dialog, Select, Checkbox } from '@dreamtree-org/twreact-ui';
import { Plus, Trash2, RefreshCw, ExternalLink, LinkIcon } from 'lucide-react';
import Can from '@rbac/Can';
import { useGitApp, errText } from './useGitApp';

const SEVERITY_VARIANT = { critical: 'error', high: 'error', medium: 'warning', low: 'secondary', info: 'default' };
const TYPES = [{ value: 'task', label: 'Task' }, { value: 'epic', label: 'Epic' }];
const selVal = (v) => (v?.target ? v.target.value : (v?.value ?? v ?? ''));

/**
 * Work items for this app: gap-scan findings and hand-written tickets.
 *
 * When a GitHub Project is linked, a new ticket is also mirrored to an Issue and added to
 * that board. The mirror is best-effort — a GitHub failure still leaves the local ticket,
 * because losing the user's typed work to an API hiccup is the worse outcome.
 */
export default function TasksTab({ appId }) {
  const { act } = useGitApp(appId);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('task');
  const [pushToBoard, setPushToBoard] = useState(true);
  const [projectUrl, setProjectUrl] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await act('tasks'));
      setErr('');
    } catch (ex) {
      setErr(errText(ex, 'Could not load tickets'));
    } finally {
      setLoading(false);
    }
  }, [act]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!title.trim()) return;
    setBusy(true);
    setErr('');
    try {
      const r = await act('createTask', { title: title.trim(), description, type, pushToBoard });
      setNotice(r.pushedToBoard ? 'Ticket created and added to the board.' : 'Ticket created.');
      setTitle('');
      setDescription('');
      setAddOpen(false);
      await load();
    } catch (ex) {
      setErr(errText(ex, 'Could not create the ticket'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setErr('');
    try {
      const r = await act('removeTasks', confirm.all ? { all: true } : { taskIds: [confirm.task.id] });
      setNotice(`${r.deleted} ticket(s) removed.`);
      setConfirm(null);
      await load();
    } catch (ex) {
      setErr(errText(ex, 'Could not remove'));
    } finally {
      setBusy(false);
    }
  };

  const setProject = async (url) => {
    setBusy(true);
    setErr('');
    try {
      const r = await act('setProject', { url });
      setNotice(r.linked ? `Linked project #${r.number} — ${r.title}.` : 'Project unlinked.');
      setLinkOpen(false);
      setProjectUrl('');
      await load();
    } catch (ex) {
      setErr(errText(ex, 'Could not link that project'));
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: 'title',
      label: 'Title',
      render: (t) => (
        <div className="flex items-center gap-2">
          {t.type === 'epic' ? <Badge variant="secondary">epic</Badge> : null}
          <span className="font-medium">{t.title}</span>
        </div>
      ),
    },
    { key: 'status', label: 'Status', render: (t) => <Badge variant={t.status === 'Done' ? 'success' : 'default'}>{t.status}</Badge> },
    { key: 'severity', label: 'Severity', render: (t) => (t.severity ? <Badge variant={SEVERITY_VARIANT[t.severity] || 'default'}>{t.severity}</Badge> : '—') },
    { key: 'source', label: 'Source', render: (t) => t.source || '—' },
    { key: 'externalRef', label: 'Ref', render: (t) => (t.externalRef ? <span className="font-mono text-[11px]">{t.externalRef}</span> : '—') },
    {
      key: 'actions',
      label: '',
      render: (t) => (
        <Can module="development" action="update">
          <button type="button" className="opacity-40 hover:opacity-100 hover:text-error-600" title="Remove" onClick={() => setConfirm({ task: t })}>
            <Trash2 size={15} />
          </button>
        </Can>
      ),
    },
  ];

  if (loading) return <Loader text="Loading tickets…" />;

  const tasks = data?.tasks || [];

  return (
    <div className="space-y-4">
      <Card className="p-3 flex items-center gap-2 flex-wrap">
        {data?.project ? (
          <a
            className="flex items-center gap-1 text-sm text-primary-600 hover:underline"
            href={data.project.url}
            target="_blank"
            rel="noreferrer"
          >
            <ExternalLink size={14} /> Project #{data.project.number} — {data.project.title}
          </a>
        ) : (
          <span className="text-sm opacity-60">No GitHub Project linked.</span>
        )}
        <Can module="development" action="update">
          <Button size="sm" variant="secondary" leftIcon={<LinkIcon size={14} />} onClick={() => setLinkOpen(true)}>
            {data?.project ? 'Change project' : 'Link project'}
          </Button>
          <Button size="sm" leftIcon={<Plus size={14} />} onClick={() => setAddOpen(true)}>New ticket</Button>
        </Can>
        <button type="button" className="opacity-50 hover:opacity-100 ml-auto" onClick={load} title="Refresh">
          <RefreshCw size={15} />
        </button>
        {tasks.length ? (
          <Can module="development" action="update">
            <button type="button" className="flex items-center gap-1 text-xs text-error-600 hover:underline" onClick={() => setConfirm({ all: true })}>
              <Trash2 size={13} /> Clear all
            </button>
          </Can>
        ) : null}
      </Card>

      {err ? <Alert variant="error" title="Tickets">{err}</Alert> : null}
      {notice ? <Alert variant="success" title="Tickets">{notice}</Alert> : null}

      {tasks.length === 0 ? (
        <Card className="p-6 text-sm opacity-60">
          No tickets yet. Run a gap scan from the Activity tab to file findings automatically,
          or add one by hand.
        </Card>
      ) : (
        <Table data={tasks} columns={columns} pagination globalSearch withAction={false} />
      )}

      {addOpen ? (
        <Dialog isOpen onClose={() => setAddOpen(false)} title="New ticket">
          <div className="space-y-3">
            <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} required />
            <Input label="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
            <Select label="Type" value={type} options={TYPES} onChange={(v) => setType(selVal(v))} />
            {data?.project ? (
              <Checkbox
                label="Also create a GitHub Issue and add it to the linked project"
                checked={pushToBoard}
                onChange={(e) => setPushToBoard(e?.target ? e.target.checked : Boolean(e))}
              />
            ) : null}
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="secondary" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button onClick={create} loading={busy} disabled={!title.trim()}>Create</Button>
            </div>
          </div>
        </Dialog>
      ) : null}

      {linkOpen ? (
        <Dialog isOpen onClose={() => setLinkOpen(false)} title="Link a GitHub Project">
          <div className="space-y-3">
            <Input
              label="Project URL"
              placeholder="https://github.com/orgs/acme/projects/4"
              value={projectUrl}
              onChange={(e) => setProjectUrl(e.target.value)}
            />
            <p className="text-[11px] opacity-60">
              A Projects v2 URL under /orgs/&lt;org&gt;/projects/&lt;n&gt; or /users/&lt;user&gt;/projects/&lt;n&gt;.
            </p>
            <div className="flex justify-end gap-2 pt-1">
              {data?.project ? (
                <Button variant="destructive" onClick={() => setProject('')} loading={busy}>Unlink</Button>
              ) : null}
              <Button variant="secondary" onClick={() => setLinkOpen(false)}>Cancel</Button>
              <Button onClick={() => setProject(projectUrl.trim())} loading={busy} disabled={!projectUrl.trim()}>Link</Button>
            </div>
          </div>
        </Dialog>
      ) : null}

      {confirm ? (
        <Dialog isOpen onClose={() => setConfirm(null)} title={confirm.all ? 'Remove every ticket?' : 'Remove this ticket?'}>
          <div className="space-y-4">
            <p className="text-sm">
              {confirm.all ? `All ${tasks.length} ticket(s) for this app are deleted.` : `"${confirm.task.title}" is deleted.`}
              {' '}Any linked board items are removed too; the underlying GitHub Issues are not closed.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirm(null)}>Cancel</Button>
              <Button variant="destructive" onClick={remove} loading={busy}>Remove</Button>
            </div>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}
